import React, { useState } from 'react';
import { Eye, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const ResultsTable = ({ results }) => {
    const [expandedRow, setExpandedRow] = useState(null);

    const toggleRowExpansion = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const formatConfidenceScore = (score) => {
        if (score === 0 || !score) return '-';
        return `${score}%`;
    };

    const getStatusIcon = (result) => {
        if (result.input_blocked) {
            return <AlertTriangle size={16} className="text-amber-500" />;
        } else if (result.output_blocked) {
            return <XCircle size={16} className="text-red-500" />;
        } else {
            return <CheckCircle size={16} className="text-green-500" />;
        }
    };

    const getStatusText = (result) => {
        if (result.input_blocked) {
            return 'Input Blocked';
        } else if (result.output_blocked) {
            return 'Output Blocked';
        } else {
            return 'Passed';
        }
    };

    const getStatusClass = (result) => {
        if (result.input_blocked) {
            return 'bg-amber-100 text-amber-800';
        } else if (result.output_blocked) {
            return 'bg-red-100 text-red-800';
        } else {
            return 'bg-green-100 text-green-800';
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-medium text-primary mb-4">Test Results</h2>

            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-300">
                                Test ID
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-300">
                                Query
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-300">
                                Status
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-300">
                                Cosine Similarity 
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-300">
                                Confidence
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-300">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((result, index) => (
                            <React.Fragment key={index}>
                                <tr className={`hover:bg-gray-50 border-b border-gray-300 ${expandedRow === index ? 'bg-blue-50' : ''}`}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {result.test_id}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                        {result.query}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(result)}`}>
                                            {getStatusIcon(result)}
                                            <span className="ml-1">{getStatusText(result)}</span>
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {result.cosine_similarity ?? '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatConfidenceScore(result.confidence_score)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => toggleRowExpansion(index)}
                                            className="text-primary hover:text-primaryDark flex items-center gap-1"
                                        >
                                            <Eye size={16} />
                                            Details
                                        </button>
                                    </td>
                                </tr>
                                {expandedRow === index && (
                                    <tr className="bg-gray-50">
                                        <td colSpan="6" className="px-6 py-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-3">
                                                    <div>
                                                        <h4 className="text-sm font-medium text-gray-700 mb-1">Query</h4>
                                                        <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-800">
                                                            {result.query}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <h4 className="text-sm font-medium text-gray-700 mb-1">Response</h4>
                                                        <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap">
                                                            {result.new_output || "No response available"}
                                                        </div>
                                                    </div>

                                                    {result.ground_truth && (
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-700 mb-1">Ground Truth</h4>
                                                            <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap">
                                                                {result.ground_truth}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="space-y-3">
                                                    {result.block_reason && (
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-700 mb-1">Block Reason</h4>
                                                            <div className="bg-red-50 p-3 rounded border border-red-100 text-sm text-red-800">
                                                                {result.block_reason}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {result.reason && (
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-700 mb-1">Reason</h4>
                                                            <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-800">
                                                                {result.reason}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {!result.input_blocked && !result.output_blocked && (
                                                        <div className="grid grid-cols-2 gap-3">
                                                            {result.accuracy != null && (
                                                                <div>
                                                                    <h4 className="text-sm font-medium text-gray-700 mb-1">Accuracy</h4>
                                                                    <div className="bg-white p-3 rounded border border-gray-200 text-sm">
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-gray-800">{result.accuracy}/40</span>
                                                                            <span className={`text-xs font-medium ${result.accuracy >= 30 ? 'text-green-600' :
                                                                                    result.accuracy >= 20 ? 'text-amber-600' : 'text-red-600'
                                                                                }`}>
                                                                                {result.accuracy >= 30 ? 'Good' :
                                                                                    result.accuracy >= 20 ? 'Average' : 'Poor'}
                                                                            </span>
                                                                        </div>
                                                                        {result.accuracy_reason && (
                                                                            <p className="mt-2 text-xs text-gray-600">{result.accuracy_reason}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {result.relevance != null && (
                                                                <div>
                                                                    <h4 className="text-sm font-medium text-gray-700 mb-1">Relevance</h4>
                                                                    <div className="bg-white p-3 rounded border border-gray-200 text-sm">
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-gray-800">{result.relevance}/40</span>
                                                                            <span className={`text-xs font-medium ${result.relevance >= 30 ? 'text-green-600' :
                                                                                    result.relevance >= 20 ? 'text-amber-600' : 'text-red-600'
                                                                                }`}>
                                                                                {result.relevance >= 30 ? 'Good' :
                                                                                    result.relevance >= 20 ? 'Average' : 'Poor'}
                                                                            </span>
                                                                        </div>
                                                                        {result.relevance_reason && (
                                                                            <p className="mt-2 text-xs text-gray-600">{result.relevance_reason}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {result.clarity != null && (
                                                                <div>
                                                                    <h4 className="text-sm font-medium text-gray-700 mb-1">Clarity</h4>
                                                                    <div className="bg-white p-3 rounded border border-gray-200 text-sm">
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-gray-800">{result.clarity}/10</span>
                                                                            <span className={`text-xs font-medium ${result.clarity >= 7 ? 'text-green-600' :
                                                                                    result.clarity >= 4 ? 'text-amber-600' : 'text-red-600'
                                                                                }`}>
                                                                                {result.clarity >= 7 ? 'Good' :
                                                                                    result.clarity >= 4 ? 'Average' : 'Poor'}
                                                                            </span>
                                                                        </div>
                                                                        {result.clarity_reason && (
                                                                            <p className="mt-2 text-xs text-gray-600">{result.clarity_reason}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {result.appropriateness != null && (
                                                                <div>
                                                                    <h4 className="text-sm font-medium text-gray-700 mb-1">Appropriateness</h4>
                                                                    <div className="bg-white p-3 rounded border border-gray-200 text-sm">
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-gray-800">{result.appropriateness}/10</span>
                                                                            <span className={`text-xs font-medium ${result.appropriateness >= 7 ? 'text-green-600' :
                                                                                    result.appropriateness >= 4 ? 'text-amber-600' : 'text-red-600'
                                                                                }`}>
                                                                                {result.appropriateness >= 7 ? 'Good' :
                                                                                    result.appropriateness >= 4 ? 'Average' : 'Poor'}
                                                                            </span>
                                                                        </div>
                                                                        {result.appropriateness_reason && (
                                                                            <p className="mt-2 text-xs text-gray-600">{result.appropriateness_reason}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ResultsTable;